// src/components/purchases/BulkRestockModal.tsx
// Modal for restocking existing bulk inventory from a purchase line item
// Shows current stock info and adds quantity from the purchase

import React, { useState } from 'react';
import { X, Package, MapPin, AlertTriangle, Plus } from 'lucide-react';
import { Product } from '../../services/productCatalogAPI';
import { BulkStock } from '../../services/inventoryInstancesAPI';
import { fulfillmentAPI } from '../../services/fulfillmentAPI';
import { PurchaseLineItem } from '../../types/purchase';

interface BulkRestockModalProps {
  isOpen: boolean;
  onClose: () => void;
  product: Product;
  existingStock: BulkStock;
  lineItem: PurchaseLineItem;
  purchaseId: string;
  onComplete: (stockId: string) => void;
}

const BulkRestockModal: React.FC<BulkRestockModalProps> = ({
  isOpen,
  onClose,
  product,
  existingStock,
  lineItem,
  purchaseId,
  onComplete
}) => {
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);

  // Calculate quantities
  const currentQuantity = existingStock.totalQuantity || 0;
  const quantityToAdd = lineItem.quantity - lineItem.allocatedQuantity;
  const newTotal = currentQuantity + quantityToAdd;
  const minStockLevel = existingStock.minStockLevel || product.minStockLevel;

  const handleConfirm = async () => {
    setLoading(true);
    setError(null);

    try {
      const response = await fulfillmentAPI.restockBulkItem({
        purchaseId,
        lineItemId: lineItem.lineItemId,
        stockId: existingStock.inventoryId,
        quantityToAdd
      });

      onComplete(response.stockId);
      onClose();
    } catch (err: any) {
      console.error('Restock failed:', err);
      setError(err.message || 'Failed to restock item');
    } finally {
      setLoading(false);
    }
  };

  if (!isOpen) return null;

  return (
    <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 p-4">
      <div className="bg-white rounded-lg shadow-xl max-w-lg w-full">
        {/* Header */}
        <div className="flex items-center justify-between p-4 border-b border-gray-200">
          <div className="flex items-center space-x-2">
            <Plus className="w-5 h-5 text-green-600" />
            <h3 className="text-lg font-semibold text-gray-900">Restock Existing Item</h3>
          </div>
          <button 
            onClick={onClose} 
            className="p-1 hover:bg-gray-100 rounded"
            disabled={loading}
          >
            <X className="w-5 h-5 text-gray-400" />
          </button>
        </div>

        {/* Content */}
        <div className="p-4 space-y-4">
          {error && (
            <div className="bg-red-50 border border-red-200 text-red-700 px-4 py-3 rounded flex items-start space-x-2">
              <AlertTriangle className="w-5 h-5 flex-shrink-0 mt-0.5" />
              <span>{error}</span>
            </div>
          )}

          {/* Product Info */}
          <div className="bg-blue-50 border border-blue-200 rounded-lg p-4">
            <div className="flex items-start space-x-3">
              <Package className="w-5 h-5 text-blue-600 mt-0.5" />
              <div>
                <h4 className="font-medium text-gray-900">{product.name}</h4>
                {(product.manufacturer || product.model) && (
                  <p className="text-sm text-gray-600">
                    {[product.manufacturer, product.model].filter(Boolean).join(' • ')}
                  </p>
                )}
              </div>
            </div>
          </div>

          {/* Current Stock Info */}
          <div className="bg-gray-50 border border-gray-200 rounded-lg p-4 space-y-3">
            <h4 className="font-medium text-gray-900 text-sm uppercase tracking-wide">Current Stock</h4>
            
            <div className="flex items-center space-x-2 text-sm">
              <MapPin className="w-4 h-4 text-gray-400" />
              <span className="text-gray-700">
                {existingStock.location}
                {existingStock.subLocation && ` - ${existingStock.subLocation}`}
              </span>
            </div>

            <div className="grid grid-cols-2 gap-4">
              <div>
                <p className="text-xs text-gray-500 uppercase">Current Quantity</p>
                <p className="text-lg font-semibold text-gray-900">
                  {currentQuantity.toLocaleString()}
                  {product.unitOfMeasure && (
                    <span className="text-sm font-normal text-gray-500 ml-1">
                      {product.unitOfMeasure}
                    </span>
                  )}
                </p>
              </div>
              {minStockLevel && (
                <div>
                  <p className="text-xs text-gray-500 uppercase">Min Stock Level</p>
                  <p className="text-lg font-semibold text-gray-900">
                    {minStockLevel.toLocaleString()}
                  </p>
                </div>
              )}
            </div>
          </div>

          {/* Restock Summary */}
          <div className="bg-green-50 border border-green-200 rounded-lg p-4">
            <h4 className="font-medium text-gray-900 text-sm uppercase tracking-wide mb-3">Restock Summary</h4>
            
            <div className="space-y-2">
              <div className="flex justify-between items-center">
                <span className="text-sm text-gray-600">Current quantity</span>
                <span className="text-sm font-medium text-gray-900">{currentQuantity.toLocaleString()}</span>
              </div>
              <div className="flex justify-between items-center">
                <span className="text-sm text-gray-600">Adding from this purchase</span>
                <span className="text-sm font-medium text-green-600">+{quantityToAdd.toLocaleString()}</span>
              </div>
              <div className="border-t border-green-300 pt-2 flex justify-between items-center">
                <span className="text-sm font-medium text-gray-900">New total</span>
                <span className="text-lg font-bold text-green-700">{newTotal.toLocaleString()}</span>
              </div>
            </div>
          </div>

          {/* Low stock warning */}
          {minStockLevel && newTotal < minStockLevel && (
            <div className="bg-amber-50 border border-amber-200 rounded-lg p-3 flex items-start space-x-2">
              <AlertTriangle className="w-5 h-5 text-amber-600 flex-shrink-0" />
              <p className="text-sm text-amber-800">
                After restocking, quantity will still be below minimum stock level ({minStockLevel.toLocaleString()})
              </p>
            </div>
          )}
        </div>

        {/* Footer */}
        <div className="flex justify-end space-x-3 p-4 border-t border-gray-200 bg-gray-50 rounded-b-lg">
          <button
            onClick={onClose}
            disabled={loading}
            className="px-4 py-2 text-gray-700 bg-white border border-gray-300 rounded-lg hover:bg-gray-50 transition-colors text-sm disabled:opacity-50"
          >
            Cancel
          </button>
          <button
            onClick={handleConfirm}
            disabled={loading}
            className="px-4 py-2 text-white bg-green-600 rounded-lg hover:bg-green-700 transition-colors text-sm disabled:opacity-50 flex items-center space-x-2"
          >
            {loading ? (
              <>
                <div className="w-4 h-4 border-2 border-white border-t-transparent rounded-full animate-spin" />
                <span>Restocking...</span>
              </>
            ) : (
              <>
                <Plus className="w-4 h-4" />
                <span>Confirm Restock</span>
              </>
            )}
          </button>
        </div>
      </div>
    </div>
  );
};

export default BulkRestockModal;
